import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Check, ExternalLink, RefreshCw, Megaphone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Admin Health Strip — passive, self-healing.
 *
 * Polls platform health probes once on mount + every 60s, and shows a
 * single actionable banner per issue. Banners auto-disappear the moment
 * the issue resolves (e.g. when the admin grants the missing Stripe
 * scope and the probe flips to `available: true`). Zero clicks needed
 * after the one-time fix.
 *
 * Today's checks:
 *  • Stripe Connect availability — instant affiliate payouts on/off
 *
 * Adding more checks later: append to `useChecks` below; keep them
 * cheap and idempotent.
 */
const STRIPE_CONNECT_FIX_URL =
  'https://dashboard.stripe.com/apikeys';

const AdminHealthStrip = ({ dataTestId = 'admin-health-strip' }) => {
  const [stripeConnect, setStripeConnect] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [blasting, setBlasting] = useState(false);
  const [blastResult, setBlastResult] = useState(null);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API_URL}/api/affiliate/connect/availability?bust=` + Date.now());
      setStripeConnect(r.data);
    } catch {
      setStripeConnect({ available: false, reason: 'probe_error' });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, []);

  // Nothing to alert about — render a tiny "all systems green" pill so
  // the admin has positive feedback the moment they fix the key.
  const allGreen = stripeConnect?.available;
  if (!stripeConnect) return null;

  const fireBlast = async (dryRun = false) => {
    if (!dryRun && !window.confirm(
      "Send the 'Instant Payouts Activated' email + WhatsApp to every active user?\n\nThis runs once per user (won't re-send to anyone already blasted)."
    )) return;
    setBlasting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/sales/payout-activation-blast?dry_run=${dryRun}`,
        {},
      );
      setBlastResult(r.data);
      toast.success(
        dryRun
          ? `Dry run: ${r.data.sent_email} would be emailed.`
          : `Sent ${r.data.sent_email} emails · ${r.data.sent_wa} WhatsApps · ${r.data.skipped} skipped.`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Blast failed');
    } finally {
      setBlasting(false);
    }
  };

  return (
    <div className="mb-6" data-testid={dataTestId}>
      {allGreen ? (
        <>
          <div
            className="flex items-center gap-2 text-xs bg-emerald-950/40 border border-emerald-700/40 text-emerald-200 px-3 py-2 rounded"
            data-testid={`${dataTestId}-all-green`}
          >
            <Check className="h-3.5 w-3.5" />
            <span className="font-semibold tracking-wide">All affiliate payout rails healthy · Stripe Connect instant payouts active</span>
            <button
              onClick={refresh}
              disabled={refreshing}
              className="ml-auto text-emerald-300 hover:text-emerald-100"
              data-testid={`${dataTestId}-recheck`}
              aria-label="Re-check"
            >
              <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Sales boost row — one-tap re-engagement of existing users */}
          <div
            className="mt-3 flex flex-wrap items-center gap-3 bg-[#0F172A]/60 border border-[#E07A3A]/40 px-4 py-3 rounded"
            data-testid={`${dataTestId}-sales-boost`}
          >
            <Megaphone className="h-4 w-4 text-[#E07A3A]" />
            <div className="text-xs text-white/85">
              <span className="font-bold uppercase tracking-wider text-[#F0A875]">Sales lever:</span>{' '}
              announce instant payouts to every existing user. One click sends email + WhatsApp.
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => fireBlast(true)}
                disabled={blasting}
                className="text-[10px] uppercase tracking-wider border border-white/20 text-white/80 hover:text-white px-3 py-1.5 rounded"
                data-testid={`${dataTestId}-blast-dryrun`}
              >
                Dry run
              </button>
              <button
                onClick={() => fireBlast(false)}
                disabled={blasting}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider bg-[#E07A3A] hover:bg-[#C4652E] text-white px-3 py-1.5 rounded font-bold"
                data-testid={`${dataTestId}-blast-send`}
              >
                {blasting ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                Send blast
              </button>
            </div>
            {blastResult && (
              <p
                className="basis-full text-[11px] text-emerald-300 mt-1"
                data-testid={`${dataTestId}-blast-result`}
              >
                ✔ {blastResult.sent_email} email · {blastResult.sent_wa} WhatsApp · {blastResult.skipped} skipped · {blastResult.failed} failed
              </p>
            )}
          </div>

          {/* Daily briefing preview — useful for testing the morning email format */}
          <div
            className="mt-3 flex flex-wrap items-center gap-3 bg-[#0F172A]/60 border border-emerald-700/40 px-4 py-3 rounded"
            data-testid={`${dataTestId}-briefing-row`}
          >
            <span className="text-emerald-300">☀</span>
            <div className="text-xs text-white/85">
              <span className="font-bold uppercase tracking-wider text-emerald-300">Daily briefing:</span>{' '}
              auto-emailed every morning at 09:00 UTC. Preview it now to test the format.
            </div>
            <button
              onClick={async () => {
                try {
                  const r = await axios.post(`${API_URL}/api/admin/sales/send-daily-briefing-now`);
                  if (r.data?.sent) toast.success('Daily briefing emailed to admin inbox.');
                  else toast.info(r.data?.reason || 'Briefing skipped — check admin email.');
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Failed to send briefing');
                }
              }}
              className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded font-bold"
              data-testid={`${dataTestId}-briefing-send`}
            >
              Send to my inbox now
            </button>
          </div>

          {/* Profit Execution Loop trigger — bounded batch=3, 30-min cron */}
          <div
            className="mt-3 flex flex-wrap items-center gap-3 bg-[#0F172A]/60 border border-[#7E22CE]/40 px-4 py-3 rounded"
            data-testid={`${dataTestId}-profit-loop-row`}
          >
            <span className="text-purple-300">∞</span>
            <div className="text-xs text-white/85">
              <span className="font-bold uppercase tracking-wider text-purple-300">Profit Loop:</span>{' '}
              runs every 30 min · picks 3 highest-intent users · sends personalised Pulse / Pro-Unlock / 50% offers.
            </div>
            <button
              onClick={async () => {
                try {
                  const r = await axios.post(`${API_URL}/api/admin/sales/profit-loop/run-now`);
                  toast.success(`Tick complete · ${r.data.sent}/${r.data.candidates} sent, ${r.data.skipped} cooled-down.`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Profit loop tick failed');
                }
              }}
              className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider bg-[#7E22CE] hover:bg-[#9333EA] text-white px-3 py-1.5 rounded font-bold"
              data-testid={`${dataTestId}-profit-loop-run`}
            >
              Run tick now
            </button>
          </div>

          {/* Force Revenue Burst — owner-only escape hatch.
              Clears the 72h cooldown, fires a profit-loop tick AND drains
              the pro-affiliate outreach queue (up to 300 sends) in one click.
              Persists the run config to `revenue_settings` so nothing is lost. */}
          <div
            className="mt-3 flex flex-wrap items-center gap-3 bg-[#7C2D12]/30 border border-[#F59E0B]/40 px-4 py-3 rounded"
            data-testid={`${dataTestId}-force-burst-row`}
          >
            <span className="text-amber-300">⚡</span>
            <div className="text-xs text-white/85">
              <span className="font-bold uppercase tracking-wider text-amber-300">Force revenue burst:</span>{' '}
              clear cooldown · bypass cadence · drain up-to-300 pro-affiliate sends with the existing approved Meta template (auto-falls back to <code className="text-amber-200">hello_world</code>).
            </div>
            <button
              onClick={async () => {
                if (!window.confirm('Force-fire profit loop + drain up to 300 pro-affiliate WhatsApp/email sends? This bypasses the 72h cooldown.')) return;
                try {
                  const r = await axios.post(`${API_URL}/api/admin/sales/force-revenue-burst`);
                  const t = r.data?.tick || {};
                  const drain = r.data?.drain_scheduled ?? 0;
                  toast.success(
                    `Burst fired · ${r.data.cooldown_cleared} cooldowns cleared · tick ${t.sent || 0}/${t.candidates || 0} · ${drain} pros queued — draining in the background, check back in ~10 min.`,
                    { duration: 12000 }
                  );
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Force burst failed — check backend logs');
                }
              }}
              className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider bg-[#F59E0B] hover:bg-[#D97706] text-[#0F172A] px-3 py-1.5 rounded font-bold"
              data-testid={`${dataTestId}-force-burst-fire`}
            >
              Fire burst now
            </button>
          </div>

          {/* Re-queue IN + CA pros that were mis-routed by the pre-fix
              phone normaliser. One-shot recovery. */}
          <div
            className="mt-3 flex flex-wrap items-center gap-3 bg-[#0B3A1A]/40 border border-emerald-700/40 px-4 py-3 rounded"
            data-testid={`${dataTestId}-requeue-misrouted-row`}
          >
            <span className="text-emerald-300">↻</span>
            <div className="text-xs text-white/85">
              <span className="font-bold uppercase tracking-wider text-emerald-300">Re-queue mis-routed pros:</span>{' '}
              flip IN + CA leads contacted before <code className="text-emerald-200">2026-05-23 06:00 UTC</code> back to <code className="text-emerald-200">pending</code> so the country-aware normaliser can re-send them on the correct +91 / +1 prefix.
            </div>
            <button
              onClick={async () => {
                if (!window.confirm('Re-queue all IN + CA pros that received the broken (mis-routed) message? They will be re-sent on the next Fire Burst / Profit-Loop tick.')) return;
                try {
                  const r = await axios.post(`${API_URL}/api/admin/sales/requeue-misrouted-pros`);
                  const b = r.data?.by_country || {};
                  toast.success(
                    `Re-queued ${r.data.requeued_total} pros · IN: ${b.IN || 0} · CA: ${b.CA || 0}. Press "Fire burst now" to send them.`,
                    { duration: 8000 }
                  );
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Re-queue failed — check backend logs');
                }
              }}
              className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded font-bold"
              data-testid={`${dataTestId}-requeue-misrouted-fire`}
            >
              Re-queue IN + CA
            </button>
          </div>
        </>
      ) : (
        <div
          className="bg-amber-950/40 border border-amber-700/40 rounded p-4"
          data-testid={`${dataTestId}-stripe-connect-issue`}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-300 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-300">
                Instant affiliate payouts disabled · 30-second fix
              </p>
              <p className="text-sm text-amber-100 mt-1 leading-relaxed">
                Your Stripe API key is missing <b>Connect → Accounts: Write</b> and{' '}
                <b>Connect → Account links: Write</b>. Affiliates currently see a "Manual payouts in 24h" badge.
                Grant those two scopes on the key and this banner self-clears within 60 seconds.
              </p>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <a
                  href={STRIPE_CONNECT_FIX_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#635BFF] hover:bg-[#4F47CC] text-white px-3 py-1.5 rounded transition"
                  data-testid={`${dataTestId}-fix-link`}
                >
                  Open Stripe API keys <ExternalLink className="h-3 w-3" />
                </a>
                <button
                  onClick={refresh}
                  disabled={refreshing}
                  className="inline-flex items-center gap-1.5 text-xs text-amber-200 hover:text-amber-50 border border-amber-700/40 px-3 py-1.5 rounded"
                  data-testid={`${dataTestId}-recheck`}
                >
                  <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
                  Re-check now
                </button>
                <span className="text-[10px] text-amber-300/70">
                  Reason: <code>{stripeConnect.reason}</code>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHealthStrip;
